import { api } from '../console/api.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, icon, person, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { supervises } from '../console/session.ts';
import { act, textInput } from '../console/ui.ts';
import { switchControl, textareaControl, textControl } from '../controls.ts';

/**
 * «Recordatorios»: what Nubi reminds, to whom and with what text (with a live preview), the
 * events it could not deliver, and what each run did.
 */

type ReminderSetting = {
  kind: string;
  label: string;
  audience: string;
  to: 'AGENCY' | 'POLICYHOLDER';
  enabled: boolean;
  editable: boolean;
  title: string;
  body: string;
  placeholders: string[];
  defaults: { title: string; body: string };
  sample: Record<string, string>;
  updatedAt: string | null;
  updatedBy: string | null;
};
type JobRun = {
  id: string;
  job: string;
  startedAt: string;
  finishedAt: string;
  businessDate: string;
  status: 'OK' | 'FAILED';
  counts: Partial<Record<'sent' | 'messages' | 'withoutConsent' | 'disabled', number>>;
  error: string | null;
};
type JobsStatus = {
  intervalMinutes: number | null;
  running: boolean;
  lastTickAt: string | null;
  jobs: { name: string; label: string; lastRun: JobRun | null; recent: JobRun[] }[];
};
/** An event that ran out of attempts (GET /v1/outbox/dead-letters). */
type DeadLetter = {
  id: number;
  type: string;
  source: string;
  attempts: number;
  lastError: string | null;
  occurredOn: string;
  createdAt: string;
  deadLetteredAt: string;
  requeues: number;
};
type DeadLetters = {
  deadLetters: DeadLetter[];
  requeues: { outboxId: number; requeuedAt: string; requeuedBy: string }[];
};
/** Who a dead letter's event was for, in the agency's words. */
const EVENT_SOURCE: Record<string, string> = {
  billing: 'Cobros y remesas',
  desk: 'Órdenes de trabajo',
  distribution: 'Onboarding',
  leads: 'Prospectos',
};
/** What an event type means to the agency (the rest keep their technical name). */
const EVENT_TYPE: Record<string, string> = {
  'InstallmentRemitted.v1': 'Recibo remesado a la aseguradora (devenga comisión)',
  'InstallmentPaidAtCarrier.v1': 'Recibo pagado en la aseguradora (devenga comisión)',
};

/** «Eventos sin entregar»: what Nubi could not pass on after every retry, and a retry button. */
function deadLettersCard(d: DeadLetters) {
  const lastBy = new Map<number, { requeuedAt: string; requeuedBy: string }>();
  for (const r of d.requeues) if (!lastBy.has(r.outboxId)) lastBy.set(r.outboxId, r);
  const head = `<div class="card-head"><h2>Eventos sin entregar</h2><span class="spacer"></span>
    <span class="muted small">Nubi reintenta cada evento varias veces. Los que siguen fallando se detienen aquí hasta que alguien los revise.</span></div>`;
  if (!d.deadLetters.length)
    return `<section class="card" id="eventos-sin-entregar">${head}<p class="muted">Todo se entregó: no hay eventos detenidos.</p></section>`;
  const rows = d.deadLetters
    .map((e) => {
      const again = lastBy.get(e.id);
      return `<tr><td>${esc(EVENT_TYPE[e.type] ?? e.type)}<div class="muted small mono">${esc(e.type)}</div></td>
        <td>${esc(EVENT_SOURCE[e.source] ?? e.source)}</td>
        <td class="nowrap">${esc(when(e.deadLetteredAt))}</td>
        <td class="num">${e.attempts}</td>
        <td>${e.lastError ? `<span class="small">${esc(e.lastError)}</span>` : '—'}${
          again
            ? `<div class="muted small">Reintentado el ${esc(when(again.requeuedAt))} por ${esc(person(again.requeuedBy))}</div>`
            : ''
        }</td>
        <td><button type="button" class="btn" data-dead-requeue="${e.id}" aria-label="Reintentar la entrega del evento ${e.id}">Reintentar</button></td></tr>`;
    })
    .join('');
  return `<section class="card" id="eventos-sin-entregar">${head}
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Evento</th><th>Área</th><th>Detenido</th><th class="num">Intentos</th><th>Último error</th><th></th></tr></thead>
    <tbody>${rows}</tbody></table></div></section>`;
}

/** The settings on screen: the live preview reads each kind's sample values from here. */
let reminderSettings: ReminderSetting[] = [];
/** Where a placeholder chip inserts, per kind: the title or the text the person last used. */
const reminderField: Record<string, HTMLInputElement | HTMLTextAreaElement> = {};

/** {placeholders} → the kind's sample values; the rest is escaped as written. */
const fillSample = (text: string, sample: Record<string, string>) =>
  esc(text).replace(/\{([a-z]+)\}/g, (m, name: string) =>
    name in sample ? `<mark>${esc(sample[name])}</mark>` : `<mark class="unknown">${m}</mark>`,
  );
const previewHtml = (r: ReminderSetting, title: string, body: string) =>
  `<strong>${fillSample(title, r.sample)}</strong><p>${fillSample(body, r.sample)}</p>`;

const reminderStateText = (on: boolean) => (on ? 'Encendido' : 'Apagado');

function reminderCard(r: ReminderSetting) {
  const k = esc(r.kind);
  const to = r.to === 'POLICYHOLDER' ? 'Al contratante' : 'A la agencia';
  const changed = r.updatedAt
    ? `<span class="muted small">Cambiado el ${esc(when(r.updatedAt))}${r.updatedBy ? ` por ${esc(person(r.updatedBy))}` : ''}</span>`
    : '';
  const editor = r.editable
    ? `<div class="reminder-edit">
        <div class="reminder-fields">
          ${field('Título', textControl({ 'data-rem-title': r.kind, maxlength: 120, value: r.title }))}
          ${field('Texto', textareaControl({ 'data-rem-body': r.kind, rows: 4, maxlength: 600, value: r.body }))}
          <div class="chips" role="group" aria-label="Datos que puedes usar">${r.placeholders
            .map(
              (p) =>
                `<button type="button" class="chip" data-rem-insert="${esc(p)}" data-kind="${k}" aria-label="Insertar el dato ${esc(p)}">{${esc(p)}}</button>`,
            )
            .join('')}</div>
        </div>
        <div class="reminder-preview"><span class="muted small">Vista previa con datos de ejemplo</span>
          <div class="preview-box" data-rem-preview="${k}" aria-live="polite">${previewHtml(r, r.title, r.body)}</div></div>
      </div>
      <div class="form-foot">${changed || '<span></span>'}<div class="inbox-actions">
        <button type="button" class="btn" data-rem-reset="${k}">Restaurar texto</button>
        <button type="button" class="btn primary" data-rem-save="${k}">Guardar texto</button></div></div>`
    : `<p class="muted small reminder-fixed">El texto de este recordatorio lo arma Nubi con los datos de cada caso.</p>`;
  return `<article class="card reminder" data-reminder="${k}"><div class="card-head">
      <div class="policy-title"><h3>${esc(r.label)}</h3><div class="policy-meta"><span>${esc(to)}</span><span>${esc(r.audience)}</span></div></div>
      <span class="spacer"></span>
      ${switchControl(
        {
          class: 'switch',
          'data-rem-enabled': r.kind,
          checked: r.enabled,
          'aria-label': `${r.label}: ${r.enabled ? 'encendido' : 'apagado'}`,
        },
        `<span data-rem-state="${k}">${reminderStateText(r.enabled)}</span>`,
      )}
    </div>${editor}</article>`;
}

function runsTable(status: JobsStatus) {
  const rows = status.jobs
    .flatMap((j) =>
      j.recent.length
        ? j.recent.map((run) => ({ label: j.label, run }))
        : [{ label: j.label, run: null as JobRun | null }],
    )
    .sort((a, b) => (b.run?.startedAt ?? '').localeCompare(a.run?.startedAt ?? ''))
    .slice(0, 30)
    .map(({ label, run }) =>
      run
        ? `<tr><td>${esc(label)}</td><td class="nowrap">${esc(when(run.startedAt))}</td><td class="nowrap">${esc(date(run.businessDate))}</td>
          <td>${run.status === 'OK' ? pill('ok', 'OK') : pill('bad', 'Falló')}${run.error ? `<div class="muted small">${esc(run.error)}</div>` : ''}</td>
          <td class="num">${run.counts.sent ?? 0}</td><td class="num">${run.counts.messages ?? 0}</td><td class="num">${run.counts.withoutConsent ?? 0}</td><td class="num">${run.counts.disabled ?? 0}</td></tr>`
        : `<tr><td>${esc(label)}</td><td colspan="7" class="muted">Aún no ha corrido</td></tr>`,
    )
    .join('');
  return `<section class="card" id="corridas"><div class="card-head"><h2>Últimas corridas</h2><span class="spacer"></span>
    <span class="muted small">${status.lastTickAt ? `Última revisión automática: ${esc(when(status.lastTickAt))}` : 'Sin revisiones automáticas todavía'}</span></div>
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Tarea</th><th>Cuándo</th><th>Fecha de negocio</th><th>Resultado</th>
    <th class="num">Enviados</th><th class="num">Mensajes</th><th class="num">Sin consentimiento</th><th class="num">Apagados</th></tr></thead>
    <tbody>${rows}</tbody></table></div></section>`;
}

/** «Recordatorios»: what Nubi reminds, to whom, with what text, and what each run did. */
export async function recordatorios() {
  if (!supervises())
    return (
      pageHead('Recordatorios', '') +
      empty(
        'lock',
        'Tu rol no configura los recordatorios',
        'La dirección y la administración de la agencia deciden qué recuerda Nubi y con qué texto.',
        '',
        2,
      )
    );
  const [settings, status, dead] = await Promise.all([
    api<ReminderSetting[]>('GET', '/v1/reminder-settings'),
    api<JobsStatus>('GET', '/v1/jobs'),
    api<DeadLetters>('GET', '/v1/outbox/dead-letters'),
  ]);
  reminderSettings = settings;
  const every =
    status.intervalMinutes === null
      ? 'En este ambiente Nubi revisa solo cuando lo pides con «Revisar ahora».'
      : `Nubi revisa cada ${status.intervalMinutes === 1 ? 'minuto' : `${status.intervalMinutes} minutos`} qué pólizas, pagos y órdenes necesitan un recordatorio, y lo envía una sola vez.`;
  let html = pageHead(
    'Recordatorios',
    `${esc(every)} Enciende o apaga cada recordatorio y ajusta su texto.`,
    `<button type="button" class="btn primary" data-jobs-run>${icon('bolt')}Revisar ahora</button>`,
  );
  html += settings.length
    ? `<section class="reminders" id="recordatorios-tipos">${settings.map(reminderCard).join('')}</section>`
    : empty(
        'bell',
        'Sin recordatorios',
        'Esta agencia todavía no tiene recordatorios configurables.',
        '',
        2,
      );
  html += deadLettersCard(dead);
  html += runsTable(status);
  return html;
}

/** Repaints one kind's preview from what the person is typing (no request). */
function paintPreview(kind: string) {
  const r = reminderSettings.find((x) => x.kind === kind);
  const box = document.querySelector<HTMLElement>(`[data-rem-preview="${CSS.escape(kind)}"]`);
  if (!r || !box) return;
  const title =
    document.querySelector<HTMLInputElement>(`[data-rem-title="${CSS.escape(kind)}"]`)?.value ?? '';
  const body =
    document.querySelector<HTMLTextAreaElement>(`[data-rem-body="${CSS.escape(kind)}"]`)?.value ??
    '';
  box.innerHTML = previewHtml(r, title, body);
}

/** Forgets the settings on screen and where the person was typing. */
export function resetReminders() {
  reminderSettings = [];
  for (const kind of Object.keys(reminderField)) delete reminderField[kind];
}

/** The kind of a reminder's title or text (data-rem-title / data-rem-body). */
const kindOf = (el: HTMLElement) => el.dataset.remTitle ?? el.dataset.remBody ?? '';
/** The live preview follows what the person types (no request). */
const preview = (el: HTMLElement) => {
  const kind = kindOf(el);
  if (kind) paintPreview(kind);
};
/** A chip inserts where the person was last typing: the title or the text. */
const remember = (el: HTMLInputElement) => {
  const kind = kindOf(el);
  if (kind) reminderField[kind] = el;
};

export const actions: Actions = {
  click: {
    deadRequeue: (t, d) => {
      if (!d.deadRequeue) return;
      const id = d.deadRequeue;
      void act(t, 'Devolviendo el evento a la cola…', async () => {
        await api('POST', `/v1/outbox/dead-letters/${encodeURIComponent(id)}/requeue`);
        await render(current, { quiet: true });
        return 'Listo: Nubi lo vuelve a intentar en los próximos segundos.';
      });
    },
    jobsRun: (t) => {
      void act(t, 'Revisando qué recordar…', async () => {
        const r = await api<{ runs: (JobRun | { status: 'LOCKED' })[] }>('POST', '/v1/jobs/run');
        await render(current, { quiet: true });
        const done = r.runs.filter((x): x is JobRun => x.status !== 'LOCKED');
        const failed = done.filter((x) => x.status === 'FAILED').length;
        const sent = done.reduce((n, x) => n + (x.counts.sent ?? 0), 0);
        if (failed)
          throw new InputError(
            `${failed} ${failed === 1 ? 'tarea falló' : 'tareas fallaron'}; revisa «Últimas corridas».`,
          );
        return sent
          ? `Revisión hecha: ${sent} ${sent === 1 ? 'recordatorio enviado' : 'recordatorios enviados'}.`
          : 'Revisión hecha: no había nada nuevo que recordar.';
      });
    },
    remInsert: (_t, d) => {
      if (!d.remInsert || !d.kind) return;
      const kind = d.kind;
      const last = reminderField[kind];
      const target =
        (last?.isConnected ? last : null) ??
        document.querySelector<HTMLTextAreaElement>(`[data-rem-body="${CSS.escape(kind)}"]`);
      if (!target) return;
      const token = `{${d.remInsert}}`;
      // The control replaces the person's selection inside it (its caret when none).
      (
        target as unknown as {
          setRangeText(t: string, s?: number, e?: number, m?: SelectionMode): void;
        }
      ).setRangeText(token, undefined, undefined, 'end');
      target.focus();
      paintPreview(kind);
    },
    remSave: (t, d) => {
      if (!d.remSave) return;
      const kind = d.remSave;
      const r = reminderSettings.find((x) => x.kind === kind);
      void act(t, 'Guardando el texto…', async () => {
        const title = textInput(`[data-rem-title="${CSS.escape(kind)}"]`, 'el título');
        const body = textInput(`[data-rem-body="${CSS.escape(kind)}"]`, 'el texto');
        const saved = await api<ReminderSetting>(
          'PUT',
          `/v1/reminder-settings/${encodeURIComponent(kind)}`,
          { title, body },
        );
        reminderSettings = reminderSettings.map((x) => (x.kind === kind ? saved : x));
        return `Texto de «${r?.label ?? kind}» guardado.`;
      });
    },
    remReset: (t, d) => {
      if (!d.remReset) return;
      const kind = d.remReset;
      const r = reminderSettings.find((x) => x.kind === kind);
      if (!r) return;
      void act(t, 'Restaurando el texto…', async () => {
        const saved = await api<ReminderSetting>(
          'PUT',
          `/v1/reminder-settings/${encodeURIComponent(kind)}`,
          r.defaults,
        );
        reminderSettings = reminderSettings.map((x) => (x.kind === kind ? saved : x));
        const ti = document.querySelector<HTMLInputElement>(
          `[data-rem-title="${CSS.escape(kind)}"]`,
        );
        const bo = document.querySelector<HTMLTextAreaElement>(
          `[data-rem-body="${CSS.escape(kind)}"]`,
        );
        if (ti) ti.value = saved.title;
        if (bo) bo.value = saved.body;
        paintPreview(kind);
        return `«${r.label}» vuelve a su texto original.`;
      });
    },
  },
  changeData: {
    remEnabled: (control) => {
      const input = control as unknown as HTMLInputElement;
      if (!input.dataset.remEnabled) return;
      const kind = input.dataset.remEnabled;
      const r = reminderSettings.find((x) => x.kind === kind);
      const on = input.checked;
      input.disabled = true;
      void act(input, on ? 'Encendiendo…' : 'Apagando…', async () => {
        try {
          const saved = await api<ReminderSetting>(
            'PUT',
            `/v1/reminder-settings/${encodeURIComponent(kind)}`,
            { enabled: on },
          );
          reminderSettings = reminderSettings.map((x) => (x.kind === kind ? saved : x));
          const state = document.querySelector<HTMLElement>(
            `[data-rem-state="${CSS.escape(kind)}"]`,
          );
          if (state) state.textContent = reminderStateText(on);
          input.setAttribute('aria-label', `${r?.label ?? kind}: ${on ? 'encendido' : 'apagado'}`);
        } catch (e) {
          input.checked = !on;
          throw e;
        } finally {
          input.disabled = false;
        }
        return on
          ? `«${r?.label ?? kind}» encendido: Nubi lo enviará cuando corresponda.`
          : `«${r?.label ?? kind}» apagado: Nubi no lo enviará.`;
      });
    },
  },
  inputData: { remTitle: preview, remBody: preview },
  focusData: { remTitle: remember, remBody: remember },
};
